import React from 'react';
import styled from 'styled-components';
import SquareRoot from '@svg/square_root';
import completionChanges from './completion.changes.md';

import { ProviderModel } from '../AssistantRouter/ProviderModel';
import { isToolModel } from '@common/Autocomplete/Base';
import { Tooltip } from '@common/Tooltip';
import { Chip, Stack, alpha } from '@mui/material';
import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useNodeError } from '@common/hooks/useNodeError';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { NodeAnchors, NodeBodyContainer, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import {
  AICompletionNodeState,
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
} from '../../';

export const config: NodeConfig = {
  version: '1.2',
  title: 'AI Completion',
  identifier: 'Completion',
  command_id: 'llm.completion',
  background: '#3434EF',
  icon: 'AI',
  description: 'Generate text using a Large Language Model of your choice.',
  changelog: completionChanges,
  anchors: [
    {
      label: 'Execute',
      id: 'llm_completion.call',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'llm_completion.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Tools',
      id: 'llm_completion.functions',
      type: 'target' as HandleType,
      variant: 'plugin',
      isHandle: true,
    },
    {
      label: 'Finished',
      id: 'llm_completion.finished',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'llm_completion.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
};

export const state: AICompletionNodeState = {
  model: '',
  name: '',
  user_id: {
    type: NodeInputType.Expression,
    value: '',
  },
  session_id: {
    type: NodeInputType.Expression,
    value: '',
  },
  message_storage: {
    type: NodeInputType.Option,
    value: 'read_and_save',
  },
  query: {
    type: NodeInputType.Expression,
    value: '',
  },
  system: {
    type: NodeInputType.Expression,
    value: '',
  },
  json_mode: {
    enabled: false,
    args: [],
  },
  message_array: {
    enabled: false,
  },
  messages: {
    type: NodeInputType.MessageExpression,
    value: '',
  },
  after: {
    tool_choice: {
      type: NodeInputType.Option,
      value: 'auto',
    },
    show_feedback: {
      type: NodeInputType.Option,
      value: 'false',
    },
    show_help_action: {
      type: NodeInputType.Option,
      value: 'false',
    },
    max_tool_calls: {
      type: NodeInputType.Text,
      value: '3',
    },
    max_tool_reached_system: {
      type: NodeInputType.Text,
      value: '',
    },
    max_conversation_history: {
      type: NodeInputType.Text,
      value: '10',
    },
  },
  low_latency_beta: 'false',
};

export const fields: NodeField[] = [
  {
    id: 'model',
    tab: '1',
    type: NodeFieldType.Option,
    default_type: NodeInputType.Model,
    label: 'Model',
    description: 'The large language model to use.',
    placeholder: 'openai/gpt-4o',
    required: true,
  },
  {
    id: 'name',
    tab: '1',
    type: NodeFieldType.Input,
    default_type: NodeInputType.Text,
    label: 'Name',
    display_name: true,
    description: 'Use this field to give your LLM a definitive name.',
    placeholder: 'Give your LLM a name',
    required: true,
  },
  {
    id: 'user_id',
    tab: 'node_extra',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'User ID',
    description: 'Specify the user identifier for logging purposes.',
    placeholder: '1234-1234-1234-1234',
    required: true,
  },
  {
    id: 'session_id',
    tab: 'node_extra',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Session ID',
    description:
      'Specify a unique identifier to enable chat history. Multiple request with the same session ID will be able to read the previous conversation history.',
    placeholder: '1234-1234-1234-1234',
  },
  {
    id: 'message_storage',
    tab: 'node_extra',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Message Storage',
    options: [
      {
        label: 'Read and Save All',
        value: 'read_and_save',
      },
      {
        label: 'Read and Save Bot Message',
        value: 'read_and_save_bot_message',
      },
      {
        label: 'Read and Save User Message',
        value: 'read_and_save_user_message',
      },
      {
        label: 'Read Only',
        value: 'read',
      },
    ],
    description: `Select the message storage option for the LLM. This option will allow the LLM to only read or read and save the message history. You can select the "read" option and save the message manually later using the Store Message node.`,
  },
  {
    id: 'query',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Query',
    description: 'This is the query that will be passed to the LLM on behalf of the customer.',
    placeholder: 'The users query',
  },
  {
    id: 'system',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'System Message',
    description: 'Define instructions for the LLM to use when writing an answer after using this function.',
    placeholder: 'Write a system message',
  },
  {
    id: 'json_mode',
    tab: '2',
    type: NodeFieldType.ToggleJSON,
    label: 'JSON Mode',
    description: 'If enabled, the LLM will output JSON attributes instead of a text response.',
  },
  {
    id: 'message_array',
    tab: '2',
    type: NodeFieldType.Toggle,
    label: 'Message Array Toggle',
    description:
      'This is the message array that will be passed to generate the LLM completion. This gives the user more fine-grained control for non-generic usecases. The query and system message fields are not used in this mode, and tools calls are handled differently. Please read the documentation labelled "Message Array" for more details.',
  },
  {
    id: 'messages',
    tab: '2',
    type: NodeFieldType.MessageArrayInputExpression,
    default_type: NodeInputType.MessageExpression,
    label: 'Messages',
    description:
      'This is the message array that will be passed to generate the LLM completion. This gives the user more fine-grained control for non-generic usecases. The query and system message fields are not used in this mode, and tools calls are handled differently. Please read the documentation labelled "Message Array" for more details.',
    placeholder: '{{ http.request.body.messages }}',
  },
  {
    id: 'after',
    tab: '3',
    type: NodeFieldType.After,
    label: 'After Execution',
    description:
      'Set the behavior of the LLM after this tool is called. You can choose to show feedback, show a help action, or call another tool.',
    placeholder: '',
  },
  {
    id: 'low_latency_beta',
    tab: '3',
    type: NodeFieldType.Toggle,
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
    label: 'Send Inference Requests Directly',
    description:
      'This option is only available to Telnyx users right now. If enabled, inference completions will be sent directly to the inference provider instead of through the public Telnyx API.',
  },
];

export const output: NodeOutput = {
  default: {
    llm: {
      completion: {
        chatbot: 'workspace_id/workflow_id',
        type: 'http',
        user_id: 'user_id',
        message_id: 'message_id',
        session_id: null,
        query: 'query',
        answer: 'answer',
        system: 'system_message',
        model: 'gpt-4-32k',
        start: 86807.34609997272,
        prompt_tokens: 18,
        completion_tokens: 9,
        metadata: {
          tool_completions: [],
          documents: [],
          processing_duration: 0,
          show_help_action: false,
          show_feedback: false,
          error: null,
          result: 'llm_result',
        },
        response_format: {
          type: 'text',
        },
        save_thread: true,
      },
    },
  },
  json_mode: {
    llm: {
      completion: {
        args: {},
      },
    },
  },
  dynamic: null,
};

const Extensions = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.text.subtext};

  padding: 0.75rem 1rem;
  border-radius: 7px 7px 0 0;
  font-family: 'Inter';

  border-bottom: 1px solid ${({ theme }) => theme.line.node};

  #label {
    color: ${({ theme }) => theme.text.subtext};
    font-size: 9px;
    font-weight: 300;
    margin-bottom: 1px;
    font-family: 'Inter';
  }

  #model {
    color: ${({ theme }) => theme.text.standard};
    font-size: 10px;
    font-weight: 400;
    font-family: 'Inter';
  }

  & .MuiBadge-badge {
    color: white;
    height: 12px;
    min-width: 12px !important;
    max-width: 12px !important;
    font-size: 8px;
    padding: 0;
    left: -10 !important;
    margin-right: 0.25rem;
    margin-top: 0.15rem;
  }

  & .MuiChip-root {
    padding: 8px;
    padding-right: 4px;
  }
`;

export const ProviderLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  height: 31px;
  width: 31px;
  border-radius: 6px;
  background-color: ${({ theme }) => alpha(theme.text.standard, 0.06)};
  border: ${({ theme }) => `1px solid ${theme.line.light}`};
`;

export const Completion: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background: _background, anchors: _anchors, icon } = config;

  const themeType = useSelector((state: State) => state.flow.theme);
  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'llm.completion') return null;

  const nodeState: AICompletionNodeState = command.state;

  const displayFieldId = command.fields.find((field) => field.display_name)?.id;
  const displayTitle = displayFieldId ? (nodeState[displayFieldId as keyof AICompletionNodeState] as string) : null;

  const jsonMode = nodeState.json_mode.enabled;
  const model = nodeState?.model || '';
  const isToolSupported = isToolModel(model);

  const anchors =
    jsonMode || !isToolSupported ? _anchors.filter((item) => item.id !== 'llm_completion.functions') : _anchors;

  const jsonArguments = jsonMode ? nodeState.json_mode.args.filter((arg) => arg.name) : [];
  const systemValue = nodeState.system.value;
  const description =
    systemValue.length > 490 ? `${systemValue.slice(0, 490).replace(/- /g, '')}...` : systemValue.replace(/- /g, '');

  const background = themeType === 'dark' ? '#5858FF' : _background;
  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle || title} icon={icon} background={background} status={nodeStatus} />
      {model && <ProviderModel model={model} />}
      {!jsonMode && nodeState.system.value && <NodeBodyContainer title={description}>{description}</NodeBodyContainer>}
      {jsonMode && (
        <Extensions>
          {jsonArguments.map(({ id, name, description, enumerator, type }) => (
            <Tooltip
              sx={{ maxWidth: '500px' }}
              title={[
                `LLM Generated Argument`,
                `\nName: ${name}`,
                `Type: ${type}`,
                description && `Description: ${description}`,
                enumerator && `Enumerator: ${enumerator}`,
              ]
                .filter(Boolean)
                .join('\n')}
              placement='top'
              arrow
              key={id}
            >
              <Chip
                onClick={() => null}
                label={name}
                color='primary'
                icon={<SquareRoot height={13} />}
                variant='outlined'
                size='small'
                sx={{ fontSize: '9px' }}
              />
            </Tooltip>
          ))}
        </Extensions>
      )}
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
